import React, { useState, useEffect, useRef } from "react";
import { Tooltip, Spinner } from "@heroui/react";
import {
  Archive,
  CaretUp,
  ArrowDown,
  Buildings,
  Cube,
  Checks,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";

interface RentalInfo {
  contact: { id: number; firstName: string; lastName: string };
  rentalContracts: Array<{
    buildingId: string;
    buildingName: string;
    unitId: string;
    unitName: string;
  }>;
}

const ConversationList = ({
  conversations,
  selectedConversation,
  setSelectedConversation,
  fetchMessages,
  fetchParticipants,
  messages,
  onLoadMore,
  hasMore,
  loadingMore,
  unreadCounts = {},
  isLoading = false,
  emptyMessage = "Geen gesprekken gevonden",
}: any) => {
  const { session } = useSession();
  const [loadedConversations, setLoadedConversations] = useState<{
    [key: string]: boolean;
  }>({});
  const [showScrollTop, setShowScrollTop] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [rentalInfoCache, setRentalInfoCache] = useState<{
    [contactId: number]: RentalInfo;
  }>({});

  const getLastMessageDirection = (
    conversation: any,
  ): "outgoing" | "incoming" | null => {
    const conversationMessages = messages[conversation.sid];
    let author: string | undefined;
    if (conversationMessages && conversationMessages.length > 0) {
      author = conversationMessages[conversationMessages.length - 1].author;
    } else if (conversation.lastMessage) {
      author = conversation.lastMessage.author;
    }
    if (!author) return null;
    return author === "system" ||
      (typeof author === "string" &&
        !author.startsWith("whatsapp:") &&
        !/^\+\d+/.test(author))
      ? "outgoing"
      : "incoming";
  };

  const formatMessageDate = (dateStr: string) => {
    if (!dateStr || dateStr === "No messages") return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();
      if (isToday) {
        return date.toLocaleTimeString("nl-BE", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      if (isYesterday) return "Gisteren";
      const diffDays = Math.floor(
        (now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000),
      );
      if (diffDays < 7) {
        return date.toLocaleDateString("nl-BE", { weekday: "long" });
      }
      return date.toLocaleDateString("nl-BE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const [fetchedContactIds, setFetchedContactIds] = useState<Set<number>>(
    new Set(),
  );

  useEffect(() => {
    const fetchRentalInfo = async () => {
      if (!session) return;
      const contactIdsToFetch: number[] = [];
      for (const conversation of conversations) {
        try {
          const attributes = JSON.parse(conversation.attributes || "{}");
          const participants = attributes.participants || [];
          for (const participant of participants) {
            const contactId = participant.contactId;
            if (
              contactId &&
              !fetchedContactIds.has(contactId) &&
              !rentalInfoCache[contactId]
            ) {
              contactIdsToFetch.push(contactId);
            }
          }
        } catch (error) {
          console.error("Error parsing conversation attributes:", error);
        }
      }
      if (contactIdsToFetch.length > 0) {
        setFetchedContactIds((prev) => {
          const newSet = new Set(prev);
          contactIdsToFetch.forEach((id) => newSet.add(id));
          return newSet;
        });
        const fetchPromises = contactIdsToFetch.map(async (contactId) => {
          try {
            const response = await fetchWithAuth(
              `${API_URL}/contact/${contactId}/rental-info`,
              { method: "GET" },
              session,
            );
            if (response.ok) {
              const rentalInfo = await response.json();
              return { contactId, rentalInfo };
            }
          } catch (error) {
            console.error(
              `Error fetching rental info for contact ${contactId}:`,
              error,
            );
          }
          return null;
        });
        const results = await Promise.all(fetchPromises);
        const newCacheEntries: { [key: number]: any } = {};
        results.forEach((result) => {
          if (result) newCacheEntries[result.contactId] = result.rentalInfo;
        });
        if (Object.keys(newCacheEntries).length > 0) {
          setRentalInfoCache((prev) => ({ ...prev, ...newCacheEntries }));
        }
      }
    };
    fetchRentalInfo();
  }, [conversations, session]);

  useEffect(() => {
    const updatedLoadedConversations = { ...loadedConversations };
    conversations.forEach((conversation: any) => {
      if (messages[conversation.sid]) {
        updatedLoadedConversations[conversation.sid] = true;
      }
    });
    setLoadedConversations(updatedLoadedConversations);
  }, [messages, conversations]);

  const handleScroll = () => {
    if (containerRef.current) {
      setShowScrollTop(containerRef.current.scrollTop > 200);
    }
  };

  const scrollToTop = () => {
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="min-h-0 flex-1 overflow-y-auto bg-white"
    >
      {conversations.length === 0 && isLoading &&
        Array.from({ length: 8 }).map((_, idx) => (
          <div
            key={`skeleton-${idx}`}
            className="flex items-center gap-3 border-b border-neutral-100 px-3 py-3"
          >
            <div className="h-12 w-12 flex-shrink-0 animate-pulse rounded-full bg-neutral-200" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-3 w-1/2 animate-pulse rounded bg-neutral-200" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-neutral-200" />
            </div>
          </div>
        ))}
      {conversations.length === 0 && !isLoading && (
        <div className="text-neutral-secondary-mid flex flex-col items-center gap-2 px-4 py-12 text-center text-sm">
          <span className="text-2xl">🔍</span>
          <span>{emptyMessage}</span>
        </div>
      )}
      {conversations.map((conversation: any) => {
        const attributes = JSON.parse(conversation.attributes || "{}");
        const participants = attributes.participants || [];
        const participantNames = participants
          .map((p: any) => p.fullName || p.identity)
          .join(", ");
        const isSelected = selectedConversation === conversation.sid;
        const unread = unreadCounts[conversation.sid] || 0;

        // Prefer fully-loaded messages (for the currently-open conversation),
        // otherwise fall back to the lightweight `lastMessage` inlined by the BE.
        const conversationMessages = messages[conversation.sid];
        const inlineLast = conversation.lastMessage;
        const lastMessageDateStr =
          conversationMessages && conversationMessages.length > 0
            ? conversationMessages[conversationMessages.length - 1].dateCreated
            : inlineLast?.dateCreated ||
              conversation.dateUpdated ||
              conversation.dateCreated;
        const lastMessageBody =
          conversationMessages && conversationMessages.length > 0
            ? conversationMessages[conversationMessages.length - 1].body ||
              "Bijlage"
            : inlineLast?.body ||
              (inlineLast?.hasMedia ? "Bijlage" : "Nog geen berichten");
        const isLoaded =
          !!conversationMessages || inlineLast !== undefined;
        const direction = getLastMessageDirection(conversation);
        const formattedDate = formatMessageDate(lastMessageDateStr);

        return (
          <div
            key={conversation.sid}
            onClick={() => {
              setSelectedConversation(conversation.sid);
              fetchMessages(conversation.sid);
              fetchParticipants(conversation.sid);
            }}
            className={`flex cursor-pointer items-center gap-3 border-b border-neutral-100 px-3 py-3 transition-colors hover:bg-neutral-50 ${
              isSelected ? "bg-neutral-100" : ""
            }`}
          >
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="bg-neutral-200 text-neutral-700 flex h-12 w-12 items-center justify-center rounded-full">
                <span className="text-sm font-semibold">
                  {participantNames
                    ? participantNames.substring(0, 2).toUpperCase()
                    : "??"}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <h3
                  className={`text-neutral-primary truncate text-[15px] ${
                    unread > 0 ? "font-semibold" : "font-medium"
                  }`}
                >
                  {participantNames || conversation.friendlyName || "Onbekend"}
                </h3>
                <span
                  className={`flex-shrink-0 text-[11px] ${
                    unread > 0
                      ? "text-moss-primary font-semibold"
                      : "text-neutral-secondary-mid"
                  }`}
                >
                  {formattedDate}
                </span>
              </div>

              <div className="mt-0.5 flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-1">
                  {direction === "outgoing" && (
                    <Checks
                      size={14}
                      weight="bold"
                      className="text-neutral-secondary-mid flex-shrink-0"
                    />
                  )}
                  {isLoaded ? (
                    <p
                      className={`truncate text-[13px] ${
                        unread > 0
                          ? "text-neutral-primary font-medium"
                          : "text-neutral-secondary-mid"
                      }`}
                    >
                      {lastMessageBody}
                    </p>
                  ) : (
                    <div className="h-3 w-3/4 animate-pulse rounded bg-neutral-200" />
                  )}
                </div>

                <div className="flex flex-shrink-0 items-center gap-1">
                  {conversation.state === "closed" && (
                    <Tooltip content="Gearchiveerd">
                      <Archive
                        size={14}
                        weight="fill"
                        className="text-neutral-secondary-mid"
                      />
                    </Tooltip>
                  )}
                  {unread > 0 && (
                    <span className="bg-moss-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold text-white">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </div>
              </div>

              {/* Rental info chips - kept compact */}
              <div className="mt-1 flex flex-wrap gap-1">
                {(() => {
                  const allRentalContracts: Array<{
                    buildingId: string;
                    buildingName: string;
                    unitId: string;
                    unitName: string;
                  }> = [];
                  participants.forEach((participant: any) => {
                    const contactId = participant.contactId;
                    if (contactId && rentalInfoCache[contactId]) {
                      allRentalContracts.push(
                        ...rentalInfoCache[contactId].rentalContracts,
                      );
                    }
                  });
                  const hasConversationLocation =
                    attributes.buildingId || attributes.unitId;
                  const hasRentalContracts = allRentalContracts.length > 0;
                  if (!hasConversationLocation && !hasRentalContracts) return null;
                  return (
                    <>
                      {attributes.buildingId && (
                        <Tooltip content="Gebouw gekoppeld aan gesprek">
                          <span className="bg-moss-primary/10 text-moss-primary inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px]">
                            <Buildings weight="fill" size={9} />
                            <span className="truncate">
                              {attributes.buildingId}
                            </span>
                          </span>
                        </Tooltip>
                      )}
                      {attributes.unitId && (
                        <Tooltip content="Unit gekoppeld aan gesprek">
                          <span className="bg-moss-primary/10 text-moss-primary inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px]">
                            <Cube weight="fill" size={9} />
                            <span className="truncate">
                              {attributes.unitId}
                            </span>
                          </span>
                        </Tooltip>
                      )}
                      {allRentalContracts
                        .slice(0, 2)
                        .map((contract, idx) => (
                          <React.Fragment
                            key={`${contract.buildingId}-${contract.unitId}-${idx}`}
                          >
                            <Tooltip
                              content={`Huurcontract: ${contract.buildingName} - ${contract.unitName}`}
                            >
                              <span className="bg-brick-primary/10 text-brick-primary inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px]">
                                <Buildings weight="fill" size={9} />
                                <span className="truncate">
                                  {contract.buildingId}
                                </span>
                              </span>
                            </Tooltip>
                          </React.Fragment>
                        ))}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })}

      {hasMore && (
        <div className="border-neutral-primary/5 flex justify-center border-t bg-white p-4">
          <ButtonNew
            variant="secondaryNeutral"
            onClick={onLoadMore}
            isDisabled={loadingMore}
            icon={
              loadingMore ? <Spinner size="sm" /> : <ArrowDown weight="duotone" />
            }
            label={loadingMore ? "Laden..." : "Laad meer"}
          />
        </div>
      )}

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="bg-moss-primary hover:bg-moss-primary/80 sticky bottom-4 left-4 z-50 flex h-10 w-10 items-center justify-center rounded-full text-white shadow-lg transition-all"
          aria-label="Scroll to top"
        >
          <CaretUp weight="bold" size={24} />
        </button>
      )}
    </div>
  );
};

export default React.memo(ConversationList);
